import { CSSProperties } from 'react'
import Icon from 'elements/Icon'
import Colors from 'modules/Colors'
import useUniversalUploadWidget from 'Hooks/useUniversalUploadWidget'
import { UploadedFile } from 'components/shareable/UniversalUploadWidget/UniversalUploadWidget'

/**
 * UploadedFilePreview - A component to display uploaded files with preview and remove functionality
 * 
 * Usage examples:
 * 
 * // Autonomous removal (uses store directly):
 * <UploadedFilePreview file={file} />
 * 
 * // Custom removal handler:
 * <UploadedFilePreview 
 *   file={file} 
 *   onRemove={(fileId) => console.log('Custom remove logic', fileId)} 
 * />
 * 
 * // No remove button:
 * <UploadedFilePreview file={file} showRemoveButton={false} />
 */

interface UploadedFilePreviewProps {
    file: UploadedFile
    layout?: 'horizontal' | 'vertical'
    onClick?: (file: UploadedFile) => void
    onRemove?: (fileId: string) => void
    showRemoveButton?: boolean
    showProgress?: boolean
    className?: string
    style?: CSSProperties
}

const UploadedFilePreview = ({
    file,
    layout = 'horizontal',
    onClick,
    onRemove,
    showRemoveButton = true, // Changed default to true since we can now handle removal internally
    showProgress = false,
    className = '',
    style = {}
}: UploadedFilePreviewProps) => {
    const { removeFile } = useUniversalUploadWidget()

    const getFileTypeDisplay = (iconType: string) => {
        switch (iconType) {
            case 'image': return 'Image'
            case 'video': return 'Video'
            case 'audio': return 'Audio'
            case 'pdf': return 'PDF'
            case 'csv': return 'CSV'
            case 'excel': return 'Excel'
            case 'word': return 'Word'
            default: return 'Document'
        }
    }

    const getFileIcon = (iconType: string) => {
        switch (iconType) {
            case 'image': return 'image'
            case 'video': return 'video' 
            case 'audio': return 'headphones'
            case 'pdf': return 'file-pdf'
            case 'csv': return 'file-csv'
            case 'excel': return 'file-excel'
            case 'word': return 'file-word'
            default: return 'file'
        }
    }

    const canPreview = (file.iconType === 'image' || file.iconType === 'video') && 
                      (file.thumbnailUrl || file.s3Url)

    const handleClick = () => {
        if (onClick && canPreview) {
            onClick(file)
        }
    }

    const handleRemove = (e: React.MouseEvent) => {
        e.stopPropagation()
        if (onRemove) {
            // Use parent's custom remove handler
            onRemove(file.id)
        } else {
            // Use store's universal remove method
            removeFile(file.id)
        }
    }

    if (layout === 'horizontal') {
        return (
            <div
                className={`uploadedFilePreview horizontal ${className}`}
                style={{
                    position: 'relative',
                    display: 'flex',
                    minWidth: 192,
                    width: 192,
                    height: 64,
                    borderRadius: 6,
                    overflow: 'hidden',
                    backgroundColor: 'white',
                    border: `1px solid ${Colors.grey}`,
                    flexShrink: 0,
                    cursor: canPreview ? 'pointer' : 'default',
                    transition: 'all 0.2s ease',
                    ...style
                }}
                onClick={handleClick}
            >
                {/* Left side - Thumbnail */}
                <div
                    style={{
                        width: 64,
                        height: 64,
                        flexShrink: 0,
                        position: 'relative',
                        backgroundColor: Colors.greyLighter
                    }}
                >
                    {(file.thumbnailUrl || (file.iconType === 'image' && file.s3Url)) ? (
                        <img
                            src={file.thumbnailUrl || file.s3Url}
                            alt={file.name}
                            style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover'
                            }}
                        />
                    ) : (
                        <div style={{
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}>
                            <Icon
                                className="fileIcon"
                                icon={getFileIcon(file.iconType)}
                                size={24}
                                color={Colors.greyDark}
                            />
                        </div>
                    )}

                    {/* Progress overlay on thumbnail */}
                    {showProgress && file.status === 'uploading' && (
                        <>
                            <div style={{
                                position: 'absolute',
                                bottom: 0,
                                left: 0,
                                right: 0,
                                height: 4,
                                backgroundColor: 'rgba(0,0,0,0.3)'
                            }}>
                                <div style={{
                                    width: `${file.uploadProgress || 0}%`,
                                    height: '100%',
                                    backgroundColor: Colors.purple,
                                    transition: 'width 0.3s ease'
                                }} />
                            </div>
                            <div style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                backgroundColor: 'rgba(0,0,0,0.5)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'white',
                                fontSize: 11,
                                fontWeight: 600
                            }}>
                                {file.uploadProgress || 0}%
                            </div>
                        </>
                    )}
                </div>

                {/* Right side - File info */}
                <div style={{
                    flex: 1,
                    padding: '8px 12px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    minWidth: 0
                }}>
                    <div style={{
                        fontSize: 12,
                        fontWeight: 500,
                        color: Colors.textColor,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        marginBottom: 2
                    }}>
                        {file.name}
                    </div>
                    <div style={{
                        fontSize: 11,
                        color: Colors.greyDark,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                    }}>
                        {getFileTypeDisplay(file.iconType)}
                        {file.size && file.size > 0 && ` • ${(file.size / 1024 / 1024).toFixed(1)} MB`}
                    </div>
                </div>

                {/* Status indicator and remove button */}
                {(showRemoveButton || file.status) && (
                    <div style={{
                        position: 'absolute',
                        top: 4,
                        right: 4,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}>
                        {file.status === 'ready' && showRemoveButton ? (
                            <div
                                style={{
                                    width: 16,
                                    height: 16,
                                    borderRadius: '50%',
                                    backgroundColor: Colors.greyLight,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer'
                                }}
                                onClick={handleRemove}
                            >
                                <Icon className="statusIcon" icon="close" size={10} color={Colors.greyDark} />
                            </div>
                        ) : file.status === 'ready' ? (
                            <div style={{
                                width: 16,
                                height: 16,
                                borderRadius: '50%',
                                backgroundColor: Colors.green,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}>
                                <Icon className="statusIcon" icon="check" size={10} color="white" />
                            </div>
                        ) : file.status === 'error' ? (
                            <div
                                style={{
                                    width: 16,
                                    height: 16,
                                    borderRadius: '50%',
                                    backgroundColor: Colors.red,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer'
                                }}
                                onClick={handleRemove}
                            >
                                <Icon className="statusIcon" icon="close" size={10} color="white" />
                            </div>
                        ) : showRemoveButton ? (
                            <div
                                style={{
                                    width: 16,
                                    height: 16,
                                    borderRadius: '50%',
                                    backgroundColor: Colors.greyLight,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer'
                                }}
                                onClick={handleRemove}
                            >
                                <Icon className="statusIcon" icon="close" size={10} color={Colors.greyDark} />
                            </div>
                        ) : null}
                    </div>
                )}
            </div>
        )
    }

    // Vertical layout (for chat messages)
    return (
        <div
            className={`uploadedFilePreview vertical ${className}`}
            style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 8,
                padding: 12,
                borderRadius: 12,
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                border: `1px solid rgba(255, 255, 255, 0.1)`,
                position: 'relative',
                maxWidth: 140,
                cursor: canPreview ? 'pointer' : 'default',
                transition: 'all 0.2s ease',
                ...style
            }}
            onClick={handleClick}
            onMouseEnter={(e) => {
                if (canPreview) {
                    e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)'
                    e.currentTarget.style.transform = 'translateY(-2px)'
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.2)'
                }
            }}
            onMouseLeave={(e) => {
                if (canPreview) {
                    e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.05)'
                    e.currentTarget.style.transform = 'translateY(0)'
                    e.currentTarget.style.boxShadow = 'none'
                }
            }}
        >
            {(file.thumbnailUrl || (file.iconType === 'image' && file.s3Url)) ? (
                <img
                    src={file.thumbnailUrl || file.s3Url}
                    alt={file.name}
                    style={{
                        width: 100,
                        height: 100,
                        objectFit: 'cover',
                        borderRadius: 8,
                        transition: 'opacity 0.2s ease'
                    }}
                />
            ) : (
                <div style={{
                    width: 100,
                    height: 100,
                    borderRadius: 8,
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: `1px solid rgba(255, 255, 255, 0.1)`
                }}>
                    <Icon
                        className="uploadedAssetIcon"
                        icon={getFileIcon(file.iconType)}
                        size={32}
                        color="rgba(255, 255, 255, 0.6)"
                    />
                </div>
            )}
            
            <span style={{
                fontSize: 12,
                color: 'rgba(255, 255, 255, 0.8)',
                textAlign: 'center',
                wordBreak: 'break-word',
                maxHeight: '2.4em',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                lineHeight: 1.2,
                fontWeight: 500
            }}>
                {file.name}
            </span>

            {/* Progress indicator for vertical layout */}
            {showProgress && file.status === 'uploading' && (
                <div style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    backgroundColor: 'rgba(255, 255, 255, 0.7)',
                    borderRadius: '50%',
                    padding: 4,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}>
                    <Icon icon="loading" size={18} className="spin" color={Colors.purple} />
                </div>
            )}
        </div>
    )
}

export default UploadedFilePreview 